import React, { useEffect } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getCategoryIconSrc } from '../marketplace/shared/marketplaceCategoryIcons';
import { highlightMarketplaceSuggestion } from '../../utils/marketplace/marketplaceSearchSuggestions';
import { HEADER_SEARCH_DROPDOWN_MOTION_SX } from '../../styles/navigation/HeaderStyles';
import { FONT_SIZES } from '../../Theme';

const SUGGESTION_ICON_SIZE = 16;

function isCategorySuggestion(item) {
  return item.kind === 'category' || item.kind === 'category-scope';
}

function HighlightedSuggestionLabel({ label, query }) {
  const tokens = highlightMarketplaceSuggestion(label, query);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {tokens.map((token) => (
        <Box
          component="span"
          key={token.id}
          sx={{
            color: token.match ? 'text.secondary' : 'text.primary',
            fontWeight: token.match ? 400 : 700,
          }}
        >
          {token.text}
        </Box>
      ))}
    </Box>
  );
}

function SuggestionRowIcon({ item }) {
  if (isCategorySuggestion(item) && item.category) {
    return (
      <ListItemIcon
        sx={{
          minWidth: 28,
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={getCategoryIconSrc(item.category)}
          alt=""
          width={SUGGESTION_ICON_SIZE}
          height={SUGGESTION_ICON_SIZE}
          aria-hidden
          sx={{
            display: 'block',
            width: SUGGESTION_ICON_SIZE,
            height: SUGGESTION_ICON_SIZE,
            objectFit: 'contain',
          }}
        />
      </ListItemIcon>
    );
  }

  return (
    <ListItemIcon
      sx={{
        minWidth: 28,
        color: 'text.secondary',
      }}
    >
      <SearchIcon sx={{ fontSize: SUGGESTION_ICON_SIZE }} />
    </ListItemIcon>
  );
}

function getSuggestionContext(item) {
  if (item.kind === 'category-scope') {
    return `in ${item.label}`;
  }
  if (item.kind === 'category') {
    return 'Category';
  }
  return null;
}

function getSuggestionPrimary(item, query) {
  if (item.kind === 'category-scope') {
    return (
      <HighlightedSuggestionLabel
        label={item.keyword || item.label}
        query={query}
      />
    );
  }
  if (item.kind === 'category') {
    return item.label;
  }
  return (
    <HighlightedSuggestionLabel label={item.label} query={query} />
  );
}

/**
 * Typed autocomplete rows attached under the chrome search input.
 * Flat option list for keyboard.
 */
function HeaderMarketplaceSearchSuggestions({
  items,
  query = '',
  onSelect,
  listId,
  activeIndex = -1,
  optionIdPrefix = 'header-marketplace-search-option',
}) {
  useEffect(() => {
    if (!items.length || activeIndex < 0) return undefined;
    const option = document.getElementById(`${optionIdPrefix}-${activeIndex}`);
    if (option && typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
    return undefined;
  }, [activeIndex, items.length, optionIdPrefix]);

  if (!items.length) return null;

  return (
    <Box
      data-testid="header-marketplace-search-suggestions"
      sx={{
        py: 0.5,
        minHeight: 0,
        flex: '1 1 auto',
        maxHeight: 'calc(100dvh - 140px)',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        ...HEADER_SEARCH_DROPDOWN_MOTION_SX,
      }}
    >
      <List
        dense
        disablePadding
        component="div"
        role="listbox"
        id={listId}
        aria-label="Search suggestions"
      >
        {items.map((item, index) => {
          const optionId = `${optionIdPrefix}-${index}`;
          const selected = index === activeIndex;
          const context = getSuggestionContext(item);

          return (
            <ListItemButton
              key={item.id}
              id={optionId}
              role="option"
              aria-selected={selected}
              disableRipple
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => onSelect(item)}
              sx={{
                mx: 0.5,
                px: 1,
                py: 0.5,
                alignItems: 'center',
                gap: 0.5,
                minHeight: 36,
                borderRadius: 1,
                bgcolor: selected ? 'background.soft' : 'transparent',
                '&:hover, &.Mui-focusVisible': {
                  outline: 'none',
                  bgcolor: 'background.soft',
                },
              }}
            >
              <SuggestionRowIcon item={item} />
              <Box
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  component="div"
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: FONT_SIZES.body,
                    fontWeight: 500,
                    color: 'text.primary',
                    lineHeight: 1.25,
                  }}
                >
                  {getSuggestionPrimary(item, query)}
                </Box>
                {context ? (
                  <Typography
                    component="span"
                    sx={{
                      flexShrink: 0,
                      fontSize: FONT_SIZES.bodySm,
                      color: 'text.secondary',
                      lineHeight: 1.25,
                    }}
                  >
                    {context}
                  </Typography>
                ) : null}
              </Box>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}

export default HeaderMarketplaceSearchSuggestions;
